import React, { useEffect, useState } from 'react';
import api from './api';
import { PROJECT_STATUS_BADGE_CLASS, PROJECT_STATUS_LABELS, projectCode, formatDate, taskCode } from './helpers';
import ProgressChart from './ProgressChart';

function Dashboard({ onSelectProject, searchQuery }) {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [echeances, setEcheances] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [detailRetard, setDetailRetard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    Promise.all([
      api.get('/reports/dashboard'),
      api.get('/projects'),
      api.get('/deadlines'),
      api.get('/progress-history'),
    ])
      .then(([statsRes, projectsRes, echRes, histRes]) => {
        if (!mounted) return;
        setStats(statsRes.data);
        setProjects(projectsRes.data);
        setEcheances(echRes.data);
        setHistorique(histRes.data);
      })
      .catch(() => {
        if (mounted) setError('Impossible de charger les données du dashboard.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  const query = (searchQuery || '').trim().toLowerCase();
  const visibleProjects = query
    ? projects.filter((p) => p.name.toLowerCase().includes(query) || projectCode(p.id).toLowerCase().includes(query) || (p.description || '').toLowerCase().includes(query))
    : projects;

  const enRetard = echeances?.overdue || [];
  const aujourdhui = echeances?.today || [];
  const bientot = echeances?.soon || [];

  return (
    <div className="fade-in">
      {(enRetard.length > 0 || aujourdhui.length > 0) && (
        <div className="alerte-retard" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              {enRetard.length > 0 && (
                <>
                  <strong>{enRetard.length} tâche{enRetard.length > 1 ? 's' : ''} en retard</strong>
                  {aujourdhui.length > 0 && ' · '}
                </>
              )}
              {aujourdhui.length > 0 && (
                <strong>{aujourdhui.length} à échéance aujourd'hui</strong>
              )}
              {bientot.length > 0 && (
                <span style={{ color: 'var(--text-secondary)' }}> · {bientot.length} dans les 7 jours</span>
              )}
            </div>
            <button className="btn btn-sm" onClick={() => setDetailRetard(!detailRetard)}>
              {detailRetard ? 'Masquer le détail' : 'Voir le détail'}
            </button>
          </div>

          {detailRetard && (
            <div style={{ marginTop: 12, borderTop: '1px solid rgba(217,83,79,0.25)', paddingTop: 10 }}>
              {[...enRetard, ...aujourdhui].slice(0, 15).map((t) => (
                <div
                  key={t.id}
                  style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '5px 0', flexWrap: 'wrap' }}
                >
                  <span style={{ color: 'var(--text-muted)', minWidth: 66 }}>{taskCode(t.id)}</span>
                  <span style={{ flex: 1, minWidth: 180 }}>{t.name}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{t.assignee_name}</span>
                  <span style={{ fontWeight: 600 }}>{formatDate(t.due_date)}</span>
                </div>
              ))}
              {enRetard.length + aujourdhui.length > 15 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  … et {enRetard.length + aujourdhui.length - 15} autres.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#517997' }} />
          <div className="stat-label">Projets actifs</div>
          <div className="stat-value">{stats.activeProjects}</div>
        </div>
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#D9534F' }} />
          <div className="stat-label">Projets en retard</div>
          <div className="stat-value">{stats.delayedProjects}</div>
        </div>
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#4CAF50' }} />
          <div className="stat-label">Projets terminés</div>
          <div className="stat-value">{stats.completedProjects}</div>
        </div>
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#8CA0B3' }} />
          <div className="stat-label">Avancement (tâches réalisées)</div>
          <div className="stat-value">{stats.overallTaskProgress}%</div>
          <div className="stat-sub">{stats.doneTasks} / {stats.totalTasks} tâches terminées</div>
        </div>
        <div className="stat-card">
          <span className="stat-dot" style={{ background: '#C19E54' }} />
          <div className="stat-label">Budget consommé</div>
          <div className="stat-value">{stats.budgetUsedPercent}%</div>
          <div className="stat-sub">{(stats.totalSpent / 1000000).toFixed(1)} / {(stats.totalBudget / 1000000).toFixed(1)} M MAD</div>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <ProgressChart points={historique} titre="Avancement du site dans le temps" />
      </div>

      <h2>Projets{query ? ` — résultats pour "${searchQuery}"` : ''}</h2>
      <div className="projects-grid">
        {visibleProjects.map((project) => (
          <div key={project.id} className="project-card" onClick={() => onSelectProject(project)}>
            <div className="project-card-top">
              <span className="project-ref">{projectCode(project.id)}</span>
              <span className={`badge ${PROJECT_STATUS_BADGE_CLASS[project.status] || 'badge-active'}`}>
                {PROJECT_STATUS_LABELS[project.status] || project.status}
              </span>
            </div>
            <div className="project-title">{project.name}</div>
            <div className="project-meta">{project.description}</div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${project.progress_percent}%` }} />
            </div>
            <div className="project-card-footer">
              <span>Avancement (tâches)</span>
              <span>{project.progress_percent}%</span>
            </div>
            <div className="progress-bar" style={{ marginTop: 4 }}>
              <div className="progress-fill" style={{ width: `${project.budget_consumed_percent || 0}%`, background: '#C19E54' }} />
            </div>
            <div className="project-card-footer">
              <span>Budget consommé</span>
              <span>{project.budget_consumed_percent || 0}%</span>
            </div>
          </div>
        ))}
        {visibleProjects.length === 0 && <div className="empty-state">Aucun projet ne correspond à la recherche.</div>}
      </div>
    </div>
  );
}

export default Dashboard;
